import React, { useState, Fragment } from 'react';
import R from 'ramda';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { HeroFullOverlay } from '@cotyorg/ccx-organism-hero';
import { CardoGroup } from '@cotyorg/ccx-organism-cardo-group';
import { CardoList } from '@cotyorg/ccx-organism-cardo-list';
import Heading from '@cotyorg/ccx-atoms/Heading';
import { Cardo } from '@cotyorg/ccx-organism-cardo';
import Picture from '@cotyorg/ccx-atoms/Picture';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import SubcategoryNavigation from '../../organisms/SubcategoryNavigation';
import StandardLayout from '../../templates/StandardLayout';
import SectionHeader from '../../atoms/SectionHeader';
import {
  categoryCardListBreakpointConfig,
  crossCategoryCardListBreakpointConfig,
  categoryCardoGroupBreakpointConfig,
} from './cardo.config';
import { isLargeBreakpoint } from '../../utils';
import ShadeFinder from '../../organisms/ShadeFinder/components/ShadeFinder';

const CategoryComponent = ({
  sectionHeadings,
  subcategoryNavigation,
  products,
  description,
  isSimpleCategoryPage,
  isCrossCategoryPage,
  currentBreakpoint,
  cardoGroup,
  shadeFinder,
}) => {
  const [vtoOpen, setVtoOpen] = useState(false);
  const [vtoProductSlug, setVtoProductSlug] = useState();

  return (
    <StandardLayout>
      <div className="category-page">
        <div className="grid-container">
          <div className="grid-x grid-margin-x">
            <div className="cell">
              <div className="hero-absolute-image">
                <HeroFullOverlay
                  isContentFirst={isLargeBreakpoint({ currentBreakpoint })}
                  heroStoreDataKey="hero"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="grid-container">
          <div className="grid-x grid-margin-x grid-padding-x">
            <div className="cell xsmall-16 large-12 large-offset-4">
              <div className="category-page__description">{description}</div>
            </div>
          </div>
        </div>

        <div className="grid-container">
          <div className="grid-x">
            <div className="cell large-4 xsmall-16">
              <div className="category-page__subcategory-navigation-wrapper">
                <SubcategoryNavigation items={subcategoryNavigation} />
              </div>
            </div>

            <div className="cell large-12 xsmall-16">
              <OptionalSection
                renderCondition={isSimpleCategoryPage}
                section={() => (
                  <CardoList
                    data={products}
                    extraClass="product-list"
                    hideDescription
                    contentTheme="transparent"
                    breakpointConfig={categoryCardListBreakpointConfig}
                    isTitleUnderlined={false}
                    isSmallTitle
                    headingLevel={5}
                    tagHeadingLevel={6}
                    fixedHeight
                    zoomOnHover
                    showMoreIconSrc="/ccx-files/assets/plus-within-circle.svg"
                    showStarRating
                    imgRatingOnSrc={{
                      fullStar: '/ccx-files/assets/star-on.svg',
                      partialStar1: '/ccx-files/assets/partial-star-1.svg',
                      partialStar2: '/ccx-files/assets/partial-star-2.svg',
                      partialStar3: '/ccx-files/assets/partial-star-3.svg',
                      partialStar4: '/ccx-files/assets/partial-star-4.svg',
                      partialStar5: '/ccx-files/assets/partial-star-5.svg',
                      partialStar6: '/ccx-files/assets/partial-star-6.svg',
                      partialStar7: '/ccx-files/assets/partial-star-7.svg',
                      partialStar8: '/ccx-files/assets/partial-star-8.svg',
                      partialStar9: '/ccx-files/assets/partial-star-9.svg',
                    }}
                    imgRatingOffSrc="/ccx-files/assets/star-off.svg"
                    showMoreIconWidth={32}
                    showMoreIconHeight={32}
                    ratingIconWidth={20}
                    ratingIconHeight={20}
                    additionalOnClick={
                      shadeFinder
                        ? ({ cardoIndex }) => {
                            setVtoOpen(true);
                            setVtoProductSlug(
                              R.path([cardoIndex, 'slug'], products)
                            );
                          }
                        : null
                    }
                  />
                )}
              />

              <OptionalSection
                renderCondition={isCrossCategoryPage}
                section={() => (
                  <div className="grid-container full">
                    <div className="grid-x grid-padding-x card-list">
                      {products.map(({ card, image }) => (
                        <Fragment key={R.prop('id', card)}>
                          <div className="cell xsmall-16 small-8 medium-5 product-list">
                            <div className="category-page__cross-category-items">
                              <Cardo
                                extraClass="category-page__cross-category-cardo"
                                data={card}
                                contentTheme="transparent"
                                isTitleUnderlined={false}
                                isSmallTitle
                                headingLevel={5}
                                tagHeadingLevel={6}
                                fixedHeight={false}
                                hideDescription
                                zoomOnHover
                                breakpointConfig={
                                  crossCategoryCardListBreakpointConfig
                                }
                              />
                            </div>
                          </div>
                          {image && (
                            <div className="cell medium-11 show-for-medium category-page__product-extra-image">
                              <Picture
                                key={R.prop('imageSrc', image)}
                                src={R.prop('imageSrc', image)}
                                srcset={R.prop('imageSrcSet', image)}
                                alt={R.prop('alt', image)}
                              />
                            </div>
                          )}
                        </Fragment>
                      ))}
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        <OptionalSection
          renderCondition={
            R.path(['cardSection1Cards', 'length'], cardoGroup) > 0
          }
          section={() => (
            <div className="grid-container">
              <div className="grid-x grid-margin-x">
                <div className="cell">
                  <SectionHeader>
                    <Heading extraClass="section__title" level={2}>
                      {R.path(['cardSection1Heading', 'heading'])(
                        sectionHeadings
                      )}
                    </Heading>

                    <Heading extraClass="section__sub-title" level={3}>
                      {R.path(['cardSection1Heading', 'introduction'])(
                        sectionHeadings
                      )}
                    </Heading>
                  </SectionHeader>
                </div>
              </div>

              <div className="grid-x grid-padding-x flex-container align-middle align-center">
                <CardoGroup
                  data="cardSection1Cards"
                  contentTheme="alpha"
                  baseBreakpointConfig={categoryCardoGroupBreakpointConfig}
                />
              </div>
            </div>
          )}
        />

        {!!shadeFinder && (
          <div className="product-page__section-try-on">
            <ShadeFinder
              shadeFinderConfiguration={shadeFinder}
              isOpen={vtoOpen}
              onClose={() => setVtoOpen(false)}
              vtoActualProductSlug={vtoProductSlug}
            />
          </div>
        )}
      </div>
    </StandardLayout>
  );
};

CategoryComponent.propTypes = {
  sectionHeadings: PropTypes.objectOf(PropTypes.object),
  subcategoryNavigation: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      url: PropTypes.string,
      productCount: PropTypes.number,
    })
  ).isRequired,
  products: PropTypes.arrayOf(PropTypes.object),
  description: PropTypes.string,
  isSimpleCategoryPage: PropTypes.bool,
  isCrossCategoryPage: PropTypes.bool,
  currentBreakpoint: PropTypes.string,
  cardoGroup: PropTypes.objectOf(PropTypes.object),
  shadeFinder: PropTypes.object,
};

export default connect(({ content, breakpoints }) => ({
  sectionHeadings: R.prop('sectionHeadings', content),
  subcategoryNavigation: R.prop('subcategoryNavigation')(content),
  products: R.path(['cards', 'products'])(content),
  sectionText: R.prop('sectionText', content),
  description: R.prop('description', content),
  isSimpleCategoryPage: R.prop('isSimpleCategoryPage', content),
  isCrossCategoryPage: R.prop('isCrossCategoryPage', content),
  currentBreakpoint: R.prop('current', breakpoints),
  cardoGroup: R.prop('cardoGroup', content),
  shadeFinder: R.prop('shadeFinder')(content),
}))(CategoryComponent);
